export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_M = 6_371_000;
/** Meters per degree of latitude (close enough at city scale). */
export const METERS_PER_DEG_LAT = 111_320;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance in meters. */
export function haversine(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Total length of a polyline in meters. */
export function pathLength(points: LatLng[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += haversine(points[i - 1], points[i]);
  return total;
}

/** The point `fraction` (0..1) of the way along a polyline, by distance. */
export function pointAlong(points: LatLng[], fraction: number): LatLng {
  if (points.length === 1 || fraction <= 0) return points[0];
  const total = pathLength(points);
  let target = Math.min(1, fraction) * total;
  for (let i = 1; i < points.length; i++) {
    const seg = haversine(points[i - 1], points[i]);
    if (target <= seg || i === points.length - 1) {
      const f = seg === 0 ? 0 : Math.min(1, target / seg);
      return {
        lat: points[i - 1].lat + (points[i].lat - points[i - 1].lat) * f,
        lng: points[i - 1].lng + (points[i].lng - points[i - 1].lng) * f,
      };
    }
    target -= seg;
  }
  return points[points.length - 1];
}

/** Offset a point by meters north/east. */
export function offsetMeters(p: LatLng, northM: number, eastM: number): LatLng {
  return {
    lat: p.lat + northM / METERS_PER_DEG_LAT,
    lng: p.lng + eastM / (METERS_PER_DEG_LAT * Math.cos(toRad(p.lat))),
  };
}

export interface Projection {
  project: (p: LatLng) => { x: number; y: number };
  /** Screen pixels back to a coordinate. */
  unproject: (pt: { x: number; y: number }) => LatLng;
  metersToPx: (m: number) => number;
  /** Pixel width of the whole Web Mercator world at this zoom (tile zoom = log2(worldPx / 512)). */
  worldPx: number;
}

/** A pan/zoom view applied on top of a fitted projection: screen = base * k + (x, y). */
export interface MapView {
  k: number;
  x: number;
  y: number;
}
export const IDENTITY_VIEW: MapView = { k: 1, x: 0, y: 0 };
export function applyView(base: Projection, view: MapView): Projection {
  if (view.k === 1 && !view.x && !view.y) return base;
  return {
    project: (p) => {
      const b = base.project(p);
      return { x: b.x * view.k + view.x, y: b.y * view.k + view.y };
    },
    unproject: ({ x, y }) =>
      base.unproject({ x: (x - view.x) / view.k, y: (y - view.y) / view.k }),
    metersToPx: (m) => base.metersToPx(m) * view.k,
    worldPx: base.worldPx * view.k,
  };
}

/** Equatorial circumference in meters (Web Mercator sphere). */
const EARTH_M = 40_075_016.686;
const mercX = (lng: number) => (lng + 180) / 360;
const mercY = (lat: number) => {
  const phi = toRad(Math.max(-85.0511, Math.min(85.0511, lat)));
  return (1 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / Math.PI) / 2;
};
const fromMercY = (y: number) =>
  (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;

/** Tile camera for a projection drawn in a width x height box. */
export function cameraOf(proj: Projection, width: number, height: number) {
  return {
    center: proj.unproject({ x: width / 2, y: height / 2 }),
    zoom: Math.log2(proj.worldPx / 512),
  };
}

/**
 * Web Mercator fit (the projection map tiles use): fit the given points into
 * a width x height box with `pad` px of margin. `minSpanM` stops a single
 * point from zooming in to street-sign level.
 */
export function fitProjection(
  points: LatLng[],
  width: number,
  height: number,
  pad: number,
  minSpanM = 600,
): Projection {
  const safe = points.length ? points : [{ lat: 37.7749, lng: -122.4194 }];
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of safe) {
    const x = mercX(p.lng);
    const y = mercY(p.lat);
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const lat0 = fromMercY(cy);
  // World units per meter at the fit's center latitude.
  const unitsPerM = 1 / (EARTH_M * Math.cos(toRad(lat0)));
  const minSpan = minSpanM * unitsPerM;
  const spanX = Math.max(maxX - minX, minSpan);
  const spanY = Math.max(maxY - minY, minSpan);
  const innerW = Math.max(1, width - pad * 2);
  const innerH = Math.max(1, height - pad * 2);
  const scale = Math.min(innerW / spanX, innerH / spanY);

  return {
    project: (p) => ({
      x: width / 2 + (mercX(p.lng) - cx) * scale,
      y: height / 2 + (mercY(p.lat) - cy) * scale,
    }),
    unproject: ({ x, y }) => ({
      lat: fromMercY(cy + (y - height / 2) / scale),
      lng: (cx + (x - width / 2) / scale) * 360 - 180,
    }),
    metersToPx: (m) => m * unitsPerM * scale,
    worldPx: scale,
  };
}
